import type { ReactNode } from 'react';
import Modal from './Modal';

export type ConfirmVariant = 'primary' | 'danger' | 'warning' | 'success';

interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: ReactNode;
  icon?: ReactNode;
  /** 강조 박스에 표시할 제목 (예: 보고서 제목) */
  highlight?: ReactNode;
  /** 강조 박스 하위 부가 설명 (예: 현장 이름) */
  highlightSub?: ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmLoadingLabel?: string;
  confirmVariant?: ConfirmVariant;
  loading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const VARIANT_CLASSES: Record<ConfirmVariant, string> = {
  primary: 'bg-brand-600 hover:bg-brand-700',
  danger: 'bg-red-600 hover:bg-red-700',
  warning: 'bg-yellow-600 hover:bg-yellow-700',
  success: 'bg-green-600 hover:bg-green-700',
};

export function ConfirmActionModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  icon,
  highlight,
  highlightSub,
  cancelLabel = '취소',
  confirmLabel = '확인',
  confirmLoadingLabel = '처리 중...',
  confirmVariant = 'primary',
  loading = false,
  disabled = false,
  size = 'md',
}: ConfirmActionModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      preventClose={loading}
    >
      <div className="flex flex-col sm:flex-row sm:items-start">
        {icon && (
          <div className="flex-shrink-0 mx-auto sm:mx-0 sm:mr-4 mb-3 sm:mb-0">
            {icon}
          </div>
        )}
        <div className="flex-1 text-center sm:text-left min-w-0">
          {highlight && (
            <div className="p-3 bg-ink-50 rounded-card">
              <p className="text-sm font-medium text-ink-900 truncate">{highlight}</p>
              {highlightSub && (
                <p className="mt-1 text-xs text-ink-500">{highlightSub}</p>
              )}
            </div>
          )}
          {message && (
            <div className={`text-sm text-ink-500 ${highlight ? 'mt-3' : ''}`}>
              {message}
            </div>
          )}
        </div>
      </div>
      <div className="mt-6 flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-ink-700 bg-white border border-ink-300 rounded-control hover:bg-ink-50 disabled:opacity-50"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading || disabled}
          className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-control disabled:opacity-50 ${VARIANT_CLASSES[confirmVariant]}`}
        >
          {loading ? confirmLoadingLabel : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export default ConfirmActionModal;
